import 'ol/ol.css';
import * as Sentry from '@sentry/react';
import Account from './components/app/Account';
import React from 'react';
import SentinelHubCallback from './components/users/detail/sentinel-hub/SentinelHubCallback';
import {AUTH_ENV, LD_CLIENTSIDE_ID} from '@account/config';
import {Client as AuthClient} from '@apps-common/auth';
import {HashRouter} from 'react-router-dom';
import {Integrations} from '@sentry/tracing';
import {asyncWithLDProvider} from 'launchdarkly-react-client-sdk';
import {createRoot} from 'react-dom/client';
import {
  handleSentinelHubCallback,
  isSentinelHubCallback,
} from './utils/sentinel-hub';
import {useGeographic as setGeographic} from 'ol/proj';

// default to CRS84 coordinate system.
setGeographic();

const redirectUri = `${window.location.origin}${window.location.pathname}`;

const authClient = new AuthClient({
  issuer: AUTH_ENV.issuer,
  clientId: AUTH_ENV.clientId,
  redirectUri,
  // ensure that all logouts with the auth client redirect back to the app root, not the sh-callback route
  postLogoutRedirectUri: redirectUri.replace(/\/sh-callback$/, ''),
});

// These Sentry environment variables are pulled in via .gitlab-ci.yml
// We currently only turn this on for prod and not for any other environments
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_SENTRY_RELEASE ?? '',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',

  // Set tracesSampleRate to capture 10% of transactions for performance
  // monitoring.
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/sampling/
  tracesSampleRate: 0.1,

  integrations: [new Integrations.BrowserTracing()],
});

// TODO: Remove this and other OAuth/user linking implementation
// after Sentinel Hub account unification is complete and user linking is unnecessary.
// https://hello.planet.com/jira/browse/CG-1271
//
// This begins a custom OAuth Auth Code with PKCE flow.
//
// Refer to https://www.oauth.com/oauth2-servers/pkce/authorization-request/
// for details on the necessary values, requests, and shape of a conforming
// implementation.
//
// This custom flow (and handling of redirects outside of the React app) is necessary due to the presence
// of the Security okta-react component which attempts to handle all OAuth redirects regardless of authorization server.
// Because we are performing OAuth flows against a different authorization server, an isolated and custom process is necessary.
//
// For Sentinel Hub user linking, the OAuth Auth Code PKCE flow will redirect back to Account app
// We check here for the expected query params, handle the token retrieval, then allow the app to load.
// On normal Account app loads, we'll pass right into .then() and load the app.

const domRoot = createRoot(document.getElementById('root'));

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: LD_CLIENTSIDE_ID,
    options: {
      application: {
        id: 'account',
      },
    },
    context: {
      kind: 'user',
      key: 'user-pl-anonymous',
    },
  });

  // We should only handle/render the SentinelHubCallback component if we are handling an OAuth callback from Sentinel Hub Keycloak
  // Otherwise, render the app as normal
  // This has the added bonus of ensuring the authClient is initialized with the correct redirect uri for either the app or callback route.
  if (isSentinelHubCallback()) {
    handleSentinelHubCallback(authClient, message =>
      domRoot.render(<SentinelHubCallback message={message} />)
    );
  } else {
    domRoot.render(
      <HashRouter>
        <LDProvider>
          <Account authClient={authClient} />
        </LDProvider>
      </HashRouter>
    );
  }
})();
